import { createStandaloneToast } from "@chakra-ui/react";
import RoutingManager from "./RoutingManager";
import Helper from "./Helper";
const { toast } = createStandaloneToast();

class HttpClient {
  constructor(options = {}) {
    this.BaseUrl = options.BaseUrl;
    this.GetAuth = options.GetAuth;
    this.SetAuth = options.SetAuth;
    this.ClearAuth = options.ClearAuth;
    this.GetLocal = options.GetLocal || (() => "en");
    this.RefreshingPromise = null;
  }

  DecodeJwt(token) {
    try {
      const base64Payload = token.split(".")[1];
      const payload = atob(base64Payload.replace(/-/g, "+").replace(/_/g, "/"));
      return JSON.parse(payload);
    } catch {
      return null;
    }
  }

  async HandleError(response) {
    const LogOutCodes = [401];
    const contentType = response?.headers?.get("Content-Type") || "";
    if (contentType.includes("json")) {
      try {
        const cloned = response.clone();
        const { error, code, message } = await cloned.json();

        if (error && LogOutCodes.includes(code)) {
          toast({
            position: "center",
            description: message,
            status: "error",
            duration: 2000,
            isClosable: true,
          });
          this.ClearAuth();
          window.location.href = "/login";
        }
      } catch (e) {
        console.error("Error parsing JSON:", e);
      }
    }
  }

  // 🔴 **التغيير هنا: استخدم Authorization بدل X-T**
  PrepareHeaders(headers = {}, { skipContentType = false } = {}) {
    const Auth = this.GetAuth();
    const newHeaders = new Headers(headers);

    newHeaders.append("pragma", "no-cache");
    newHeaders.append("cache-control", "no-cache");
    newHeaders.append("X-Requested-With", "true");
    newHeaders.append("Local", this.GetLocal());

    if (Auth?.UserAccessToken) {
      newHeaders.set("X-T", `Bearer ${Auth.UserAccessToken}`);
    } else {
      const tokenFromStorage = localStorage.getItem("token");
      if (tokenFromStorage) {
        newHeaders.set("X-T", `Bearer ${tokenFromStorage}`);
      }
    }

    if (!skipContentType && !newHeaders.has("Content-Type")) {
      newHeaders.set("Content-Type", "application/json");
    }
    if (skipContentType) {
      newHeaders.delete("Content-Type");
    }
    return newHeaders;
  }

  async RefreshTokenIfNeeded() {
    const auth = this.GetAuth();
    if (!auth?.UserAccessToken) return;
    
    const decoded = this.DecodeJwt(auth.UserAccessToken);
    const now = Math.floor(Date.now() / 1000);
    
    if (decoded?.exp && decoded.exp - now < 30) {
      if (!this.RefreshingPromise) {
        this.RefreshingPromise = (async () => {
          try {
            const response = await fetch(`${this.BaseUrl}/auth/refresh`, {
              method: "PATCH",
              headers: { 
                "Content-Type": "application/json",
                "X-T": `Bearer ${auth.UserRefreshToken}`
              },
              
              body: JSON.stringify({ Token: auth.UserRefreshToken }),
            });

            if (response.ok) {
              const data = await response.json();
              const newToken =
                data?.data?.UserAccessToken ||
                data?.UserAccessToken ||
                data?.token;

              if (newToken) {
                this.SetAuth({
                  ...auth,
                  UserAccessToken: newToken,
                });
              }
            }
          } catch (error) {
            console.error("Refresh token error:", error);
          } finally {
            this.RefreshingPromise = null;
          }
        })();
      }

      await this.RefreshingPromise;
    }
  }

  // Main Request Handler
  async Request(url, options = {}) {

    await this.RefreshTokenIfNeeded();

    const finalUrl = url.startsWith("http") ? url : this.BaseUrl + url;
    const isFormData = options.body instanceof FormData;
    const headers = this.PrepareHeaders(options.headers, { skipContentType: isFormData });

    const response = await fetch(finalUrl, {
      ...options,
      headers,

    });
   
    if (response.status !== 401) {
      this.HandleError(response);
    }
    
    if (response.status === 401) {
    
      const auth = this.GetAuth();
      if (auth?.UserRefreshToken) {
        try {
          const refreshResponse = await fetch(`${this.BaseUrl}/auth/refresh`, {
            method: "PATCH",
            headers: { 
              "Content-Type": "application/json",
              "X-T": `Bearer ${auth.UserRefreshToken}`
            },
            
            body: JSON.stringify({ refreshToken: auth.UserRefreshToken }),
          });
          
          const data = await refreshResponse.json();
          const newToken = data?.data?.UserAccessToken;
          
          if (newToken) {
            this.SetAuth({
              ...auth,
              UserAccessToken: newToken,
            });

            // Retry original Request
            return await fetch(finalUrl, {
              ...options,
              headers: this.PrepareHeaders(options.headers, { skipContentType: isFormData }),
            });
          }
        } catch (error) {
          console.error("Refresh failed:", error);
        }
      }
      
      // إذا فشل refresh، امسح كل حاجة
      await fetch(`${this.BaseUrl}/auth/logout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
      });
      
      this.ClearAuth();
      
      // Show toast and redirect
      toast({
        title: "Session Expired",
        description: "Please login again",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      
      setTimeout(() => {
        // window.location.href = "/login";
      }, 2000);
    }
    
    return response;
  }

  // Methods
  Get(url, params, options = {}) {
    const qs = params ? "?" + new URLSearchParams(params).toString() : "";
    return this.Request(url + qs, {
      method: "GET",
      ...options,
    });
  }

  Post(url, body, options = {}) {
    return this.Request(url, {
      method: "POST",
      body: JSON.stringify(body),
      ...options,
    });
  }
  
  PostFormData(url, body, options = {}) {
    // FormData لا يحتاج Content-Type header
    const headers = new Headers(options.headers || {});
    headers.delete("Content-Type"); // fetch سيتعامل معه تلقائياً
    
    return this.Request(url, {
      method: "POST",
      headers,
      body,
      ...options,
    });
  }
  
  Put(url, body, options = {}) {
    return this.Request(url, {
      method: "PUT",
      body: JSON.stringify(body),
      ...options,
    });
  }
  
  Patch(url, body, options = {}) {
    return this.Request(url, {
      method: "PATCH",
      body: JSON.stringify(body),
      ...options,
    });
  }
  
  Delete(url, body, options = {}) {
    return this.Request(url, {
      method: "DELETE",
      body: JSON.stringify(body),
      ...options,
    });
  }
}

let Client = null;
function GetHttpClient(Url = null) {
  if (Client) return Client;
  
  Client = new HttpClient({
    BaseUrl: Url || RoutingManager.ApiUrl,
    GetAuth: () => {
      // 1. جرب من Helper
      const userFromHelper = Helper.GetStorage("User");
      if (userFromHelper?.UserAccessToken) {
        return userFromHelper;
      }
      
      // 2. جرب من localStorage مباشرة
      const token = localStorage.getItem("token");
      const userStr = localStorage.getItem("User");
      
      if (token && userStr) {
        try {
          const user = JSON.parse(userStr);
          return {
            ...user,
            UserAccessToken: token
          };
        } catch (error) {
          console.error("Error parsing user:", error);
        }
      }
      
      return null;
    },
    SetAuth: (NewAuth) => {
      // حفظ في Helper
      Helper.SetStorage("User", NewAuth);
      
      // حفظ في localStorage أيضاً
      if (NewAuth?.UserAccessToken) {
        localStorage.setItem("token", NewAuth.UserAccessToken);
      }
      localStorage.setItem("User", JSON.stringify(NewAuth));
    },
    ClearAuth: () => {
      Helper.ClearStorage("User");
      localStorage.removeItem("token");
      localStorage.removeItem("User");
    },
    GetLocal: () => Helper.GetStorage("Lang") || "en",
  });
  
  return Client;
}

export { HttpClient, GetHttpClient };